<script>
import reportsService from "@/services/reports";

/**
 * @component ReportView
 * @description Renderiza un dashboard embebido de Metabase. Le pide al
 *              backend la URL firmada (JWT con TTL corto) y la mete en un
 *              iframe.
 *
 *              Si el JWT expira mientras el usuario tiene el iframe abierto,
 *              Metabase devuelve 401 — re-cargamos la URL automático con un
 *              refresh cada 8 min (el TTL del backend es 10).
 */
export default {
  name: "ReportView",

  data() {
    return {
      isLoading: false,
      embedUrl: null,
      error: null,
      refreshTimer: null,
    };
  },

  computed: {
    /**
     * @description La key del dashboard viene del path param de la ruta.
     * @returns {string}
     */
    dashboardKey() {
      return this.$route.params.key;
    },
  },

  watch: {
    "$route.params.key"() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  beforeDestroy() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },

  methods: {
    /**
     * @description Pide al backend la URL firmada del dashboard. Programa un
     *              refresh ~2 min antes de que el JWT expire para no cortar
     *              al usuario en medio de la navegación.
     * @returns {Promise<void>}
     */
    async load() {
      this.isLoading = true;
      this.error = null;
      try {
        const { url, expiresAt } = await reportsService.getDashboardEmbedUrl(
          this.dashboardKey
        );
        this.embedUrl = url;
        this.scheduleRefresh(expiresAt);
      } catch (err) {
        this.error =
          err?.response?.data?.error ||
          err?.message ||
          "No pudimos cargar este reporte.";
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Programa el próximo refresh del iframe ~2 min antes del
     *              expiry del JWT. Sirve para sesiones largas donde el
     *              usuario tiene el dashboard abierto mucho tiempo.
     * @param {number} expiresAt - timestamp en ms del expiry del JWT
     */
    scheduleRefresh(expiresAt) {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      const now = Date.now();
      const refreshIn = Math.max(60000, expiresAt - now - 2 * 60 * 1000);
      this.refreshTimer = setTimeout(() => this.load(), refreshIn);
    },

    /**
     * @description Volver a la lista de reportes.
     */
    goBack() {
      this.$router.push({ name: "reports-list" });
    },
  },
};
</script>

<template lang="pug">
section.report-view-page
  .page-header
    a.back-link(@click="goBack") ← Volver a Reportes
  spinner(v-if="isLoading")
  .error-state(v-else-if="error")
    p {{ error }}
    button.btn-secondary(@click="load") Reintentar
  iframe.report-iframe(
    v-else-if="embedUrl"
    :src="embedUrl"
    frameborder="0"
    allow-transparency="true"
  )
</template>

<style lang="scss" scoped>
.report-view-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8fafc;
}
.page-header {
  padding: 12px 24px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}
.back-link {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.back-link:hover {
  color: #002d5e;
}
.error-state {
  padding: 48px;
  text-align: center;
  color: #b45309;
  background: #fffbeb;
  margin: 24px;
  border-radius: 12px;
  border-left: 4px solid #d97706;
}
.error-state p {
  margin: 0 0 16px;
  font-size: 14px;
}
.btn-secondary {
  padding: 8px 18px;
  border-radius: 8px;
  background: #fff;
  color: #0a3d7a;
  font-weight: 700;
  font-size: 13px;
  border: 1.5px solid #e2e8f0;
  cursor: pointer;
}
.report-iframe {
  flex: 1;
  width: 100%;
  border: none;
  min-height: 600px;
}
</style>
